import { RotateCw } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * `<RefreshButton>` — Phase 09 icon-only glass-strong refresh trigger.
 * Used by AmenityCell, CategoryHeader, and CountyRow for per-cell, per-
 * column, and per-row refresh respectively.
 *
 * `ariaLabel` is REQUIRED — describes the scope ("Refresh dog parks in
 * Pinellas County"). Plan 09-03 wires onClick to useTriggerRefresh.mutate.
 *
 * pending=true disables the button and swaps the icon to a spinner via
 * `animate-spin`. Glass-strong styling (foam-50/10 base) — secondary
 * action, NOT the recompute primary CTA. The focus-visible ring uses
 * the standard outline-bronze accent (slot #7, audit-allowed).
 */
export interface RefreshButtonProps {
  /** aria-label describing the refresh scope (required for a11y). */
  ariaLabel: string;
  onClick: () => void;
  pending?: boolean;
  className?: string;
}

export function RefreshButton({
  ariaLabel,
  onClick,
  pending = false,
  className,
}: RefreshButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={pending}
      onClick={() => onClick()}
      className={cn(
        'inline-flex items-center justify-center w-8 h-8 rounded-lg',
        'bg-foam-50/10 hover:bg-foam-50/15 border border-white/10',
        'text-foam-50/80 hover:text-foam-50',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        pending && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <RotateCw
        aria-hidden="true"
        className={cn('w-4 h-4', pending && 'animate-spin')}
      />
    </button>
  );
}
